import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import type { EventInfo, Me } from '@/lib/api'
import { RegistrationForm } from '@/pages/play/RegistrationForm'
import { GameScreen } from '@/game/GameScreen'
import { ErrorNotice } from '@/components/ErrorNotice'

type State =
  | { kind: 'loading' }
  | { kind: 'register'; event: EventInfo }
  | { kind: 'playing'; me: Me }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }

/**
 * /play/:eventSlug
 * Resolves the authoritative state from the server on every load:
 *  - valid session for this event  -> resume the game
 *  - otherwise                     -> show registration for the event
 */
export function PlayPage() {
  const { eventSlug = '' } = useParams()
  const [state, setState] = useState<State>({ kind: 'loading' })

  const resolve = useCallback(async () => {
    setState({ kind: 'loading' })
    try {
      try {
        const me = await api.me()
        if (me.event.slug === eventSlug) {
          setState({ kind: 'playing', me })
          return
        }
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) throw err
      }
      const { event } = await api.event(eventSlug)
      setState({ kind: 'register', event })
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setState({ kind: 'not-found' })
      else setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [eventSlug])

  useEffect(() => {
    void resolve()
  }, [resolve])

  switch (state.kind) {
    case 'loading':
      return (
        <main>
          <p className="muted" role="status">
            Loading…
          </p>
        </main>
      )
    case 'not-found':
      return (
        <main>
          <h1>Event not found</h1>
          <p className="muted">Check the link you were given.</p>
        </main>
      )
    case 'error':
      return (
        <main>
          <ErrorNotice message={state.message} onRetry={() => void resolve()} />
        </main>
      )
    case 'register':
      return <RegistrationForm event={state.event} onRegistered={(me) => setState({ kind: 'playing', me })} />
    case 'playing':
      return <GameScreen me={state.me} onLoggedOut={() => void resolve()} />
  }
}
